<template>
  <div class="slider_box">
    <!-- 当前值 -->
    <div class="slider_value" :style="{ left: percent }">{{ current }}%</div>

    <!-- 轨道 -->
    <div class="slider_track" ref="track" @mousedown="onTrackClick">
      <div class="slider_progress" :style="{ width: percent }"></div>
      <div class="slider_default" />
      <!-- 拖拽点 -->
      <div
        class="slider_thumb"
        :style="{ left: percent }"
        @mousedown.stop="startDrag"
      >
        <div class="slider_thumb_middle">
          <div class="slider_thumb_con" />
        </div>
      </div>
    </div>

    <!-- 刻度 -->
    <div class="slider_steps">
      <span
        v-for="step in steps"
        :key="step"
        :style="{ left: getStepPercent(step) }"
        @click="snapTo(step)"
      >
        {{ step }}%
      </span>
    </div>
  </div>
</template>

<script>
export default {
  name: "DiscreteSlider",

  props: {
    value: {
      type: Number,
      default: 25,
    },
    steps: {
      type: Array,
      default: () => [0, 1, 5, 10, 15, 25, 50],
    },
    max: {
      type: Number,
      default: 50,
    },
  },

  data() {
    return {
      dragging: false,
      current: this.value,
      timeoutId: null,
    };
  },

  computed: {
    percent() {
      // 使用与刻度相同的映射规则
      if (this.current === 0) {
        return "14px";
      } else if (this.current === 50) {
        return "calc(100% - 10px)"; //
      } else if (this.current === 1) {
        return 8 + "%";
      } else if (this.current <= 25) {
        return 8 + ((this.current - 1) / 24) * ((2 / 3) * 100 - 8) + "%";
      } else {
        return (2 / 3) * 100 + ((this.current - 25) / 25) * (1 / 3) * 100 + "%";
      }
    },
  },

  watch: {
    value(val) {
      this.current = val;
    },
  },

  mounted() {
    document.addEventListener("mousemove", this.onDrag);
    document.addEventListener("mouseup", this.stopDrag);
    this.snapTo(this.current);
  },

  beforeDestroy() {
    document.removeEventListener("mousemove", this.onDrag);
    document.removeEventListener("mouseup", this.stopDrag);
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  },

  methods: {
    startDrag() {
      this.dragging = true;
    },

    stopDrag() {
      this.dragging = false;
    },

    onDrag(e) {
      if (!this.dragging) return;

      const rect = this.$refs.track.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const ratio = Math.max(0, Math.min(x / rect.width, 1));

      // 反向计算实际值（从百分比转换为实际值）
      let rawValue;
      if (ratio <= 0.08) {
        // 0-8%映射到0-1
        rawValue = (ratio / 0.08) * 1;
      } else if (ratio <= 2 / 3) {
        // 8%-66.67%映射到1-25
        rawValue = 1 + ((ratio - 0.08) / (2 / 3 - 0.08)) * 24;
      } else {
        // 66.67%-100%映射到25-50
        rawValue = 25 + ((ratio - 2 / 3) / (1 / 3)) * 25;
      }

      this.snapTo(rawValue);
    },

    onTrackClick(e) {
      const rect = this.$refs.track.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const ratio = Math.max(0, Math.min(x / rect.width, 1));

      // 反向计算实际值（从百分比转换为实际值）
      let rawValue;
      if (ratio <= 0.08) {
        // 0-8%映射到0-1
        rawValue = (ratio / 0.08) * 1;
      } else if (ratio <= 2 / 3) {
        // 8%-66.67%映射到1-25
        rawValue = 1 + ((ratio - 0.08) / (2 / 3 - 0.08)) * 24;
      } else {
        // 66.67%-100%映射到25-50
        rawValue = 25 + ((ratio - 2 / 3) / (1 / 3)) * 25;
      }

      this.snapTo(rawValue);
    },

    snapTo(val) {
      // 吸附到最近刻度
      const nearest = this.steps.reduce((prev, curr) =>
        Math.abs(curr - val) < Math.abs(prev - val) ? curr : prev,
      );

      this.current = nearest;
      if (this.timeoutId) clearTimeout(this.timeoutId);
      this.timeoutId = setTimeout(() => {
        this.$emit("changeRuler", nearest);
      }, 300);
    },

    getStepPercent(step) {
      // 调整映射规则：0-1占更大的间隔，1-25占剩余的2/3，25-50占1/3
      if (step === 0) {
        return "14px";
      } else if (step === 50) {
        return "calc(100% - 20px)"; //
      } else if (step === 1) {
        return 8 + "%"; // 0-1之间的间隔较大
      } else if (step <= 25) {
        // 1-25的部分占据从8%到67%（59%的空间）
        return 8 + ((step - 1) / 24) * ((2 / 3) * 100 - 8) + "%";
      } else {
        // 25-50的部分占据从67%到100%
        return (2 / 3) * 100 + ((step - 25) / 25) * (1 / 3) * 100 + "%";
      }
    },
  },
};
</script>

<style scoped>
.slider_box {
  width: 100%;
  padding-top: 38px;
  height: auto;
  user-select: none;
  position: relative;
}

.slider_value {
  font-family: "Arial Bold";
  font-weight: normal;
  font-size: 24px;
  color: #3fb92f;
  line-height: 30px;
  text-align: center;
  position: absolute;
  transform: translate(-50%, 0);
  top: 10px;
}

.slider_track {
  cursor: pointer;
  width: 100%;
  height: 26px;
  background: #dae4dc;
  box-shadow: inset 0px 2px 4px 1px rgba(38, 90, 47, 0.57);
  border-radius: 18px;
  border: 3px solid #eeeeee;
  padding: 7px 14px;
  position: relative;
}
.slider_default {
  width: 100%;
  height: 100%;
  height: 6px;
  background: #70b067;
  box-shadow: inset 0px 1px 3px 0px rgba(9, 76, 0, 0.68);
  border-radius: 4px;
}
.slider_progress {
  position: absolute;
  height: 100%;
  height: 6px;
  background: linear-gradient(270deg, #3fb92f 0%, #178e08 100%);
  border-radius: 4px;
}

.slider_thumb {
  position: absolute;
  top: 50%;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  transform: translate(-50%, -50%);
  background: linear-gradient(180deg, #ffffff 0%, #d2d2d2 100%);
  box-shadow: 0px 1px 4px 2px rgba(61, 63, 66, 0.2);
  cursor: pointer;
}
.slider_thumb_middle {
  position: absolute;
  top: 50%;
  border-radius: 50%;
  width: 26px;
  height: 26px;
  transform: translate(calc(-50% + 15px), -50%);
  background: linear-gradient(180deg, #cacaca 0%, #ffffff 100%);
  box-shadow: 0px 0px 2px 0px rgba(0, 0, 0, 0.2);
  border-image: linear-gradient(
      180deg,
      rgba(183, 183, 183, 1),
      rgba(255, 255, 255, 1)
    )
    1 1;
}
.slider_thumb_con {
  position: absolute;
  top: 50%;
  border-radius: 50%;
  width: 17px;
  height: 17px;
  transform: translate(calc(-50% + 13px), -50%);
  background: linear-gradient(306deg, #178e08 0%, #3fb92f 100%);
  border-image: linear-gradient(
      157deg,
      rgba(63, 185, 47, 1),
      rgba(18, 122, 5, 1)
    )
    1 1;
}
.slider_steps {
  margin-top: 10px;
  position: relative;
  height: 12px;
  font-size: 12px;
  color: #666666;
  line-height: 26px;
  text-align: center;
  font-style: normal;
}

.slider_steps span {
  position: absolute;
  cursor: pointer;
  transform: translateX(-50%);
  white-space: nowrap;
  margin-left: 5px;
  margin-right: 5px;
}
.slider_steps span::after {
  content: "";
  width: 1px;
  height: 6px;
  background: #999999;
  position: absolute;
  top: -6px;
  left: 8px;
}
</style>
